// The navigation of a layout described as data, so the top bar (TopNav) and the side rail (SideNav) render the same
// items with the same permission and active rules.

export interface NavItem {
    key: string
    /** Icon class, e.g. 'mdi mdi-map' */
    icon?: string
    title: string
    /** Path to navigate to */
    target?: string
    /** Called instead of navigating */
    onClick?: () => void
    active?: boolean
    /** A group: a dropdown in the top bar, a group with its items below it in the side rail */
    children?: NavItem[]
}

export interface NavAccount {
    title: string
    subTitle?: string
    /** Items of the account menu */
    links: NavItem[]
}

export interface Navigation {
    /** The main items: left in the top bar, at the top of the side rail */
    items: NavItem[]
    /** Help, settings: icons at the right of the top bar, at the bottom of the side rail */
    endItems: NavItem[]
    account: NavAccount
}

/**
 * A group with the children that are allowed (falsy ones are left out). A group with one child becomes that child,
 * still active by the rule of the group, like NavBarLink did with a single link.
 */
export function navGroup(group: NavItem, children: (NavItem | false | null | undefined)[]): NavItem {
    const allowed = children.filter((child): child is NavItem => !!child)

    if (allowed.length === 1) {
        return {...allowed[0], key: group.key, active: group.active}
    }

    return {...group, children: allowed}
}

/** Leaves out the items that aren't allowed */
export function navItems(items: (NavItem | false | null | undefined)[]): NavItem[] {
    return items.filter((item): item is NavItem => !!item)
}

/** The branding of the reseller: undefined while it loads (grey placeholders), {} for the Veloyd look */
export interface NavBarBranding {
    squareLogo?: string
    /** Colour of the bar under the active link, instead of the brand colour */
    color?: string
}

/** An entry of the nav bar or one of its menus: goes to target (with history) or calls onClick */
export interface NavBarLinkItem {
    icon?: string
    title?: string
    /** The accessible name of an icon-only entry (no title) */
    label?: string
    /** NavBarMenu: a second line under the title */
    subTitle?: string
    target?: string
    onClick?: () => void
    /** In a menu: shown in a medium font weight */
    active?: boolean
}

/** The part of react-router's history the nav bar uses */
export interface NavBarHistory {
    push: (path: string) => void
}
